<template>
    <DForm @submit.prevent="submitForm">
        <input
            type="hidden"
            :value="form.id"
        >
        <input
            type="hidden"
            :value="form.contract_id"
        >

        <DFormRow
            :label="__('customers.contracts.start_date')"
            :is-required="true"
            :error="errors.start_date"
        >
            <DDateFormatInput
                id="start_date"
                v-model="form.start_date"
                format="YYYY-MM-DD"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.contracts.price')"
            :is-required="true"
            :error="errors.price"
        >
            <DInput
                id="price"
                v-model="form.price"
                maxlength="12"
                :disabled="!userPermissions.includes('price.edit')"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.contracts.third_party_cost')"
            :is-required="true"
            :error="errors.third_party_cost"
        >
            <DInput
                id="price"
                v-model="form.third_party_cost"
                maxlength="12"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.contracts.billing_cycle')"
            :is-required="true"
            :error="errors.billing_cycle"
        >
            <DSelect
                id="billing_cycle"
                v-model="form.billing_cycle"
                :options="billingCycles"
                :placeholder="__('customers.contracts.billing_cycle')"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.contracts.setup_fee')"
            :is-required="true"
            :error="errors.setup_fee"
        >
            <DInput
                id="setup_fee"
                v-model="form.setup_fee"
                maxlength="12"
            />
        </DFormRow>
        <ProductChangesSelection
            v-if="isDomainContract === false"
            :label="__('customers.contracts.product')"
            :selected-products="selectedProduct"
            :product-id="activeProductId"
            :ignore-current="false"
            :multiple="false"
            :required="true"
            @update="updateProductId"
        />
        <DFormRow
            v-else
            :label="__('products.products.form.name')"
            :error="errors.product_id"
        >
            <DSelect
                id="product"
                :options="selectedProduct"
                disabled="true"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.contracts.contract_term')"
            :is-required="true"
            :error="errors.contract_term"
        >
            <DInput
                id="contract_term"
                v-model="form.contract_term"
                type="number"
                min="0"
                max="127"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.contracts.contract_term_unit')"
            :is-required="true"
            :error="errors.product_id"
        >
            <DSelect
                id="contract_term_unit"
                v-model="form.contract_term_unit"
                :options="contractTermUnits"
                placeholder="Select a contract term unit"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.contracts.discount_name')"
            :is-required="true"
            :error="errors.discount_name"
        >
            <DInput
                id="discount_name"
                v-model="form.discount_name"
                maxlength="64"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.contracts.discount_type')"
            :error="errors.discount_type"
        >
            <DSelect
                id="discount_type"
                v-model="form.discount_type"
                :options="discountTypes"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.contracts.discount_value')"
            :is-required="!!form.discount_type"
            :error="errors.discount_value"
        >
            <DInput
                id="discount_value"
                v-model="form.discount_value"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.contracts.notice_period')"
            :is-required="true"
            :error="errors.notice_period"
        >
            <DInput
                id="notice_period"
                v-model="form.notice_period"
                type="number"
                min="0"
                max="127"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.contracts.notice_period_unit')"
            :is-required="true"
            :error="errors.notice_period_unit"
        >
            <DSelect
                id="notice_period_unit"
                v-model="form.notice_period_unit"
                :options="contractTermUnits"
                placeholder="Select a notice period unit"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.contracts.phase_text')"
            :is-required="true"
            :error="errors.phase_text"
        >
            <DInput
                id="phase_text"
                v-model="form.phase_text"
            />
        </DFormRow>

        <DFormControls
            v-model="form.activity_log_comment"
            :cancel-route="$route('customers.customers.contracts.phases.index', {customer: customerId, contract: form.contract_id})"
            :dirty="form.isDirty"
            :show-change-reason-input="true"
            :disabled="isDisabled"
            @submit="submitForm"
        />
    </DForm>
</template>

<script>
import ProductChangesSelection from '~@/Components/Products/Products/ProductChangesSelection'
import priceFormat from '~@/Mixins/priceFormat'

export default {
    name: 'ContractPhaseForm',
    components: { ProductChangesSelection },
    mixins: [
        priceFormat
    ],
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        customerId: {
            type: Number,
            default: 0
        },
        contract: {
            type: Object,
            default: () => { return {} }
        },
        contractPhase: {
            type: Object,
            default: () => { return {} }
        },
        billingCycles: {
            type: Array,
            default: () => { return [] }
        },
        contractTermUnits: {
            type: Array,
            default: () => { return [] }
        },
        discountTypes: {
            type: Array,
            default: () => { return [] }
        },
        isDomainContract: {
            type: Boolean,
            default: false
        },
        activeProductId: {
            type: Number,
            default: 0
        }
    },
    emits: ['submit'],
    data () {
        return {
            form: this.$inertia.form({
                id: this.contractPhase.id,
                contract_id: this.contract.id,
                start_date: this.contractPhase.start_date,
                price: this.contractPhase?.price ? this.numberFormatForInput(this.contractPhase.price) : null,
                third_party_cost: this.contractPhase?.third_party_cost ? this.numberFormatForInput(this.contractPhase.third_party_cost) : null,
                billing_cycle: this.contractPhase.billing_cycle,
                setup_fee: this.contractPhase?.setup_fee ? this.numberFormatForInput(this.contractPhase.setup_fee) : null,
                product_id: this.contractPhase.product_id,
                contract_term: this.contractPhase.contract_term,
                contract_term_unit: this.contractPhase.contract_term_unit,
                discount_name: this.contractPhase.discount_name,
                discount_type: this.contractPhase.discount_type,
                discount_value: this.contractPhase?.discount_value ? this.numberFormatForInput(this.contractPhase.discount_value) : null,
                notice_period: this.contractPhase.notice_period,
                notice_period_unit: this.contractPhase.notice_period_unit,
                phase_text: this.contractPhase.phase_text,
                activity_log_comment: ''
            })
        }
    },
    computed: {
        selectedProduct () {
            if (this.contractPhase.product) {
                return [{
                    value: this.contractPhase.product.id,
                    label: `${this.contractPhase.product.name}${this.contractPhase.product.retired ? ' (' + this.__('products.products.index.retired') + ')' : ''} (${this.contractPhase.product.id}) - (${this.contractPhase.product.product_group.name})`
                }]
            }

            return []
        },
        isDisabled () {
            // If the id is null, enable all fields because we want to create a new
            // record and don't want to edit an existing one.
            if (this.form.id === null) {
                return false
            }

            if (typeof this.errors.start_date !== 'undefined') {
                return true
            }

            if (this.form.id === this.contract.active_contract_phase.id && this.contract.invoice_items?.length === 0) {
                return false
            }

            // makes sure that a contract phase can not be edited if it has a start date in the past
            return new Date(this.form.start_date) <= new Date()
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        }
    },
    methods: {
        submitForm () {
            this.$emit('submit', this.form)
        },
        updateProductId (value) {
            this.form.product_id = value.value
        }
    }
}
</script>
